// src/components/layout/PageContainer.tsx
import clsx from 'clsx';
import type { ReactNode, ElementType } from 'react';

type Width =
  | 'container'
  | 'container-sm'
  | 'container-md'
  | 'container-lg'
  | 'container-xl'
  | 'container-xxl'
  | 'container-fluid';

type MaxWidthToken = 'none' | 'prose' | 'sm' | 'md' | 'lg' | 'xl' | number | string;

const MAX_MAP: Record<Exclude<MaxWidthToken, number | 'none'>, string> = {
  // 文字閱讀建議用（約 70ch）
  prose: '70ch',
  sm: '640px',
  md: '700px',
  lg: '760px',
  xl: '840px',
};

function resolveMax(max: Exclude<MaxWidthToken, 'none'>) {
  if (typeof max === 'number') return `${max}px`;
  return MAX_MAP[max as keyof typeof MAX_MAP] ?? max; // 支援 '72ch'、'42rem' 等
}

interface Props {
  children: ReactNode;
  /** 預設用 <main>，注意全頁只應有一個 <main> */
  as?: ElementType;
  /** 外層 Bootstrap 寬度容器，預設 'container' */
  width?: Width;
  className?: string;
  /**
   * 單頁最大寬（預設 1280px）
   * - 可用：數字(px)、'72ch'、'42rem'、'prose'、'md'...
   * - 傳 'none' 可完全關閉限制
   */
  maxWidth?: MaxWidthToken;
  /** 內層容器 class（只有在有 maxWidth 時才會用到） */
  contentClassName?: string;
}

export default function PageContainer({
  children,
  as: Tag = 'main',
  width = 'container',
  className,
  maxWidth = 1280, // ← 預設 1280px
  contentClassName,
}: Props) {
  // 關閉 max 限制：直接回傳外層容器
  if (maxWidth === 'none') {
    return <Tag className={clsx(width, 'pt-10 pb-20', className)}>{children}</Tag>;
  }

  const resolved = resolveMax(maxWidth);

  return (
    <Tag className={clsx(width, 'pt-2 pt-md-10 pb-20 min-vh-100', className)}>
      {/* 內層：置中並套最大寬 */}
      <div
        className={clsx('mx-auto', contentClassName)}
        style={{ maxWidth: resolved, width: '100%' }}
      >
        {children}
      </div>
    </Tag>
  );
}
